<script lang="ts">
export default {
  name: "FrontendNavigation",
};
</script>

<script lang="ts" setup>
import { type LocationQueryRaw, useRouter } from "vue-router";
import { useFleetRouteCheck } from "@/frontend/composables/useFleetRouteCheck";
import { useI18n } from "@/shared/composables/useI18n";
import AppNavigation from "@/shared/components/AppNavigation/index.vue";
import NavItem from "@/shared/components/AppNavigation/NavItem/index.vue";

import ScDataSourceSwitch from "@/frontend/components/ScDataSource/index.vue";

import FleetNav from "./FleetNav/index.vue";
import NotificationsNav from "./NotificationsNav/index.vue";
import CatalogueNav from "@/frontend/components/Navigation/CatalogueNav/index.vue";
import FleetsNav from "./FleetsNav/index.vue";
import ToolsNav from "./ToolsNav/index.vue";
import { usePendingFriendRequests } from "@/frontend/composables/usePendingFriendRequests";
import { useSessionStore } from "@/frontend/stores/session";
import { useHangarStore } from "@/frontend/stores/hangar";
import { useFiltersStore } from "@/shared/stores/filters";
import { storeToRefs } from "pinia";
import rsiLogo from "@/images/rsi_logo.png";
import favicon from "@/images/favicon-small.png";

const { t } = useI18n();

const sessionStore = useSessionStore();

const { isAuthenticated, currentUser } = storeToRefs(sessionStore);

const hangarStore = useHangarStore();

// Friend requests are answered two levels in, under settings, so the footer
// row that leads there carries their count.
const { count: pendingFriendRequests } = usePendingFriendRequests();

const { preview: hangarPreview } = storeToRefs(hangarStore);

const { isFleetRoute } = useFleetRouteCheck();

const filtersStore = useFiltersStore();

const { filters } = storeToRefs(filtersStore);

const route = useRoute();

/*
 * Vite mode, not NODE_ENV: a build sets NODE_ENV to "production" whatever the
 * mode, so the old gate hid these routes on stage too. Mode is Rails.env, so
 * stage and the e2e build keep them and only live strips them.
 */
const visualTestsEnabled = import.meta.env.MODE !== "production";

const VisualTestsNav = visualTestsEnabled
  ? defineAsyncComponent(() => import("./VisualTestsNav/index.vue"))
  : undefined;

const isVisualTestsRoute = computed(() => {
  if (import.meta.env.MODE === "production") {
    return false;
  }

  if (!route.name) {
    return false;
  }

  return String(route.name).includes("visual-tests");
});

const isShipRoute = computed(() => {
  if (!route.name) {
    return false;
  }

  return ["ships", "ship", "ship-images", "ship-videos"].includes(
    String(route.name),
  );
});

const isHangarRoute = computed(() => {
  if (!route.name) {
    return false;
  }

  // Every hangar route is named from its path, so they all lead with it. A
  // match anywhere in the name also lit the hangar up on `settings-hangar`.
  return String(route.name).startsWith("hangar");
});

const filterFor = (routeName: string) => {
  if (!filters.value[routeName]) {
    return undefined;
  }

  return {
    q: filters.value[routeName],
  } as unknown as LocationQueryRaw;
};

const router = useRouter();

const logout = () => {
  void sessionStore.logout();
  void router.replace({ name: "login" });
};

const settingsActive = computed(() => {
  return [
    "settings-profile",
    "settings-account",
    "settings-hangar",
    "settings-donations",
    "settings-features",
    "settings-notifications",
    "settings-friends",
    "settings-friends-incoming",
    "settings-friends-outgoing",
    "settings-friends-ignored",
    "settings-security-status",
    "settings-two-factor-enable",
    "settings-two-factor-disable",
    "settings-two-factor-backup-codes",
    "settings-change-password",
    "settings-connections",
    "settings-oauth-applications",
    "settings-oauth-application",
    "settings-oauth-application-create",
    "settings-oauth-application-edit",
  ].includes(String(route.name));
});
</script>

<template>
  <AppNavigation :title="t('title.default')" :logo="favicon">
    <template #main>
      <VisualTestsNav v-if="isVisualTestsRoute" />
      <FleetNav v-else-if="isFleetRoute" />
      <template v-else>
        <NavItem
          :to="{ name: 'home' }"
          :label="t('nav.home')"
          icon="fa-duotone fa-home-alt"
        />
        <NavItem
          v-if="isAuthenticated || !hangarPreview"
          :to="{
            name: 'hangar',
            query: filterFor('hangar'),
          }"
          :label="t('nav.hangar.index')"
          :active="isHangarRoute"
          icon="fa-duotone fa-warehouse"
        />
        <NavItem
          v-else
          :to="{ name: 'hangar-preview' }"
          :label="t('nav.hangar.index')"
          icon="fa-light fa-warehouse"
        />
        <NavItem
          :to="{
            name: 'ships',
            query: filterFor('ships'),
          }"
          :label="t('nav.ships.index')"
          :active="isShipRoute"
          icon="fa-duotone fa-starship"
        />
        <!-- Route meta alone does not put anything here: both menus are written
             out by hand and read none of it, so `nav: "main"` on the route left
             the catalogue reachable only by typing the address. -->
        <CatalogueNav />
        <NavItem
          :to="{ name: 'compare' }"
          :label="t('nav.compare.ships')"
          icon="fa-duotone fa-code-compare"
        />
        <FleetsNav />
        <NavItem
          :to="{ name: 'images' }"
          :label="t('nav.images')"
          icon="fa-duotone fa-images"
        />
        <ToolsNav />
        <NavItem
          :to="{ name: 'stats' }"
          :label="t('nav.stats')"
          icon="fa-duotone fa-chart-bar"
        />
        <template v-if="visualTestsEnabled">
          <li class="nav-item__divider" />
          <NavItem
            :to="{ name: 'visual-tests' }"
            :label="t('nav.visualTests.index')"
            icon="fa-duotone fa-pen-swirl"
          />
        </template>
      </template>
    </template>
    <template #footer>
      <ScDataSourceSwitch />
      <template v-if="isAuthenticated && currentUser">
        <NotificationsNav />
        <NavItem
          :to="{ name: 'settings' }"
          :active="settingsActive"
          :label="t('nav.settings.index')"
          icon="fa-light fa-cog"
          :badge="pendingFriendRequests"
        />
        <template v-if="currentUser.rsiHandle">
          <NavItem
            :href="`https://robertsspaceindustries.com/citizens/${currentUser.rsiHandle}`"
            :label="t('nav.rsiProfile')"
            :image="rsiLogo"
          />
        </template>
        <NavItem
          :action="logout"
          menu-key="logout"
          :label="t('nav.logout')"
          icon="fa-light fa-sign-out"
        />
        <NavItem
          menu-key="user-menu"
          :image="currentUser.avatar?.smallUrl"
          :avatar="true"
          :label="currentUser.username"
          class="user-menu mt-1"
          data-test="user-menu"
        />
      </template>
      <NavItem
        v-else
        key="user-menu-guest"
        :to="{ name: 'login' }"
        :label="t('nav.login')"
        icon="fa-light fa-sign-in"
      />
    </template>
  </AppNavigation>
</template>

<style lang="scss" scoped>
@import "index";
</style>
